import { useState } from "react";
import { ChevronLeft, ChevronRight, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/data/stat-card";
import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { EmptyState } from "@/components/data/empty-state";
import { PageToolbar } from "@/components/layout/page-toolbar";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow, DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import {
  useMensalistasDaCompetencia, usePendenciasFechamento, useGerarFechamentos, useAtualizarFechamento,
  type LinhaMensalistaMes,
} from "@/hooks/use-equipe";
import { MESES_LABEL } from "@/components/equipe/nota-mensal";
import { BlocoPendencias } from "@/components/equipe/mes/BlocoPendencias";
import { ExtratoMensalistaSheet } from "@/components/equipe/mes/ExtratoMensalistaSheet";
import {
  LinhaMensalistaMesDesktop, LinhaMensalistaMesMobile, totaisDoMes,
  COL_ESTADO_FIXA, COL_ACAO_FIXA, FIXA_CABECALHO, FIXA_TOTAL,
} from "@/components/equipe/mes/LinhaMensalistaMes";
import { msgDoErro } from "@/components/equipe/mes/msg-do-erro";
import {
  formatarBRL, formatarDataCurta, paraReais, proximaCompetencia, resumirMes, estadoLinhaMes,
  rotuloKpiAdiantamentos,
  type EstadoDoMes,
} from "@shared/equipe-fechamento";

/** "Agosto 2026" — o rótulo do navegador de competência. */
export function nomeDaCompetencia(competencia: string): string {
  const nome = MESES_LABEL[Number(competencia.slice(5, 7)) - 1] ?? competencia;
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)} ${competencia.slice(0, 4)}`;
}

/** Competência anterior — `proximaCompetencia` só anda para a frente. */
function competenciaAnterior(competencia: string): string {
  const ano = Number(competencia.slice(0, 4));
  const mes = Number(competencia.slice(5, 7));
  const d = new Date(Date.UTC(ano, mes - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

const ESTADO_MES: Record<EstadoDoMes, { texto: string; tom: Tom }> = {
  projecao: { texto: "PROJEÇÃO", tom: "neutro" },
  gerado: { texto: "GERADO", tom: "atencao" },
  pago: { texto: "PAGO", tom: "sucesso" },
  vazio: { texto: "SEM MENSALISTA", tom: "neutro" },
};

/**
 * Aba "Mensalistas · mês" do Financeiro (v3.1, D-M1) — responde "quanto cada
 * mensalista recebe no fechamento desta competência?", no calendário do MÊS
 * CIVIL. Arquivo próprio de propósito: `financeiro.tsx` só ganha o `PageTabs`
 * e o switch.
 *
 * Nada aqui cria diária nem calcula um número "parecido": líquido é
 * `liquidoLinhaMes` (snapshot ou `calcLiquidoFechamento`), estado é
 * `estadoDoMes`, KPIs são `resumirMes` — tudo em shared/equipe-fechamento.ts.
 */
export function FinanceiroMes({ competencia, onCompetencia }: { competencia: string; onCompetencia: (c: string) => void }) {
  // PR 2: gerar/pagar fechamento é `equipe.folha.fechar` (ADMIN e GESTOR).
  const isAdmin = usePode("equipe.folha.fechar");
  const { toast } = useToast();
  const { data, isLoading, error } = useMensalistasDaCompetencia(competencia);
  // A lista de pendências do SERVIDOR — o mesmo predicado que trava o PATCH.
  const { data: pendenciasData } = usePendenciasFechamento(competencia);
  const gerar = useGerarFechamentos();
  const atualizar = useAtualizarFechamento();
  const [aberta, setAberta] = useState<LinhaMensalistaMes | null>(null);
  const [pagandoTodos, setPagandoTodos] = useState(false);

  const linhas = data?.linhas ?? [];
  const resumo = resumirMes(linhas);
  const pendencias = pendenciasData?.pendencias ?? [];
  const idsPendentes = new Set(pendencias.map(p => p.funcionarioId));
  const quinzenas = data?.quinzenasDaCompetencia ?? [];
  const quinzenasAbertas = quinzenas.filter(q => q.status === "ABERTA");
  const podeGerar = resumo.estado === "projecao" && quinzenas.length > 0 && quinzenasAbertas.length === 0;
  const dataPagamento = data?.dataPagamento ?? "";
  const totais = totaisDoMes(linhas);
  // Rótulo e valor do KPI de adiantamentos saem do MESMO `resumo` (um número, uma função).
  const kpiAdiantamentos = rotuloKpiAdiantamentos(resumo);
  const linhaAberta = aberta ? linhas.find(l => l.id === aberta.id) ?? aberta : null;

  const onGerar = async () => {
    try {
      const r = await gerar.mutateAsync(competencia);
      toast({ title: `Fechamento de ${nomeDaCompetencia(competencia)} gerado (${r?.criados ?? 0} linha${r?.criados === 1 ? "" : "s"}).` });
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao gerar o fechamento"), variant: "destructive" });
    }
  };

  // D-M6 — "Pago" = PATCH {status:'FECHADO'}; a trava ADIANTAMENTO_PENDENTE é do servidor.
  const marcarPago = async (l: LinhaMensalistaMes): Promise<boolean> => {
    if (!l.fechamento) return false;
    try {
      await atualizar.mutateAsync({ id: l.fechamento.id, data: { status: "FECHADO" } });
      return true;
    } catch (e) {
      toast({ title: `${l.apelido ?? l.nome}: ${msgDoErro(e, "não foi possível marcar como pago")}`, variant: "destructive" });
      return false;
    }
  };

  // "Marcar todos como pagos" = a mesma chamada, uma por linha ABERTA, em
  // sequência, com toast por recusa — sem endpoint em lote (D-M6).
  const marcarTodos = async () => {
    setPagandoTodos(true);
    let ok = 0;
    try {
      for (const l of linhas) {
        if (estadoLinhaMes(l) !== "aberto") continue;
        if (await marcarPago(l)) ok += 1;
      }
    } finally {
      setPagandoTodos(false);
    }
    if (ok > 0) toast({ title: `${ok} fechamento${ok === 1 ? "" : "s"} marcado${ok === 1 ? "" : "s"} como pago${ok === 1 ? "" : "s"}.` });
  };

  const faixaEstado = (() => {
    const e = ESTADO_MES[resumo.estado];
    let texto: React.ReactNode;
    if (resumo.estado === "projecao" || resumo.estado === "vazio") {
      const datas = quinzenasAbertas.map(q => formatarDataCurta(q.dataPagamento ?? q.periodoFim));
      texto = datas.length > 0
        ? <>mês em andamento — valores mudam com a presença e com {datas.length === 1 ? "a quinzena" : "as quinzenas"} de {datas.join(" e ")}</>
        : quinzenas.length === 0
          ? <>nenhuma quinzena começa em {nomeDaCompetencia(competencia).toLowerCase()} — crie a quinzena no Financeiro para o mês existir</>
          : <>todas as quinzenas do mês fechadas — pronto para gerar o fechamento</>;
    } else if (resumo.estado === "gerado") {
      const geradoEm = linhas.map(l => l.fechamento?.criado_em).filter(Boolean).sort()[0];
      texto = <>gerado{geradoEm ? ` em ${formatarDataCurta(geradoEm)}` : ""} · aguardando pagamento de <strong>{formatarBRL(paraReais(resumo.liquidoAPagarCentavos))}</strong></>;
    } else {
      const pagoEm = linhas.map(l => l.fechamento?.fechado_em).filter(Boolean).sort().slice(-1)[0];
      texto = <><strong>{formatarBRL(paraReais(resumo.liquidoPagoCentavos))}</strong> pago{pagoEm ? ` em ${formatarDataCurta(pagoEm)}` : ""}</>;
    }
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs" data-testid="faixa-estado-mes">
        <StatusBadge tom={e.tom} ponto>{e.texto}</StatusBadge>
        <span className="text-muted-foreground min-w-0 flex-1">{texto}</span>
        {isAdmin && resumo.estado === "gerado" && (
          <Button size="sm" className="hidden md:inline-flex" disabled={pagandoTodos || atualizar.isPending} onClick={marcarTodos} data-testid="btn-marcar-todos-pagos">
            {pagandoTodos && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Marcar todos como pagos
          </Button>
        )}
        {isAdmin && resumo.estado === "projecao" && (
          <Button
            size="sm"
            variant="outline"
            className="hidden md:inline-flex"
            disabled={!podeGerar || gerar.isPending}
            onClick={onGerar}
            data-testid="btn-gerar-fechamento"
          >
            <Lock className="h-3.5 w-3.5 mr-1" />
            {podeGerar ? "Gerar fechamento" : quinzenasAbertas.length > 0
              ? `Gerar fechamento (após fechar a quinzena de ${formatarDataCurta(quinzenasAbertas[quinzenasAbertas.length - 1].dataPagamento ?? quinzenasAbertas[quinzenasAbertas.length - 1].periodoFim)})`
              : "Gerar fechamento"}
          </Button>
        )}
      </div>
    );
  })();

  return (
    <div className="space-y-4 pb-28 md:pb-6">
      {/* Cabeçalho da aba: ‹ Agosto 2026 › · Paga em sáb 05/09 · N mensalistas */}
      <PageToolbar
        fim={
          <p className="text-xs text-muted-foreground text-right">
            Paga em <strong className="text-foreground">{formatarDataCurta(dataPagamento)}</strong>
            <span className="hidden sm:inline"> · {resumo.nMensalistas} mensalista{resumo.nMensalistas === 1 ? "" : "s"}</span>
          </p>
        }
      >
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => onCompetencia(competenciaAnterior(competencia))} aria-label="Mês anterior" data-testid="btn-mes-anterior">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-bold whitespace-nowrap min-w-[8rem] text-center" data-testid="competencia-atual">{nomeDaCompetencia(competencia)}</span>
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => onCompetencia(proximaCompetencia(competencia))} aria-label="Mês seguinte" data-testid="btn-mes-seguinte">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="cards" />
      ) : error ? (
        <EmptyState titulo="Não foi possível carregar o mês" descricao={msgDoErro(error, "Tente de novo em instantes.")} />
      ) : (
        <>
          {/* KPIs — 4 → 2×2 no celular, nunca capada. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            <StatCard
              rotulo="Líquido a pagar"
              valor={formatarBRL(paraReais(resumo.liquidoAPagarCentavos))}
              legenda={resumo.estado === "pago" ? `${formatarBRL(paraReais(resumo.liquidoPagoCentavos))} já pago` : "salários − adiantamentos − descontos"}
              testId="kpi-liquido"
            />
            <StatCard rotulo="Salários" valor={formatarBRL(paraReais(resumo.salariosCentavos))} legenda={`${resumo.nMensalistas} mensalista${resumo.nMensalistas === 1 ? "" : "s"}`} testId="kpi-salarios" />
            <StatCard
              rotulo={kpiAdiantamentos.rotulo}
              valor={`− ${formatarBRL(paraReais(resumo.adiantamentosCentavos))}`}
              legenda={
                <>
                  {kpiAdiantamentos.legendaVermelha
                    ? <span className="text-erro font-semibold">{kpiAdiantamentos.legenda}</span>
                    : kpiAdiantamentos.legenda}
                  {kpiAdiantamentos.aGerar && <> · <span className="text-erro font-semibold">{kpiAdiantamentos.aGerar}</span></>}
                </>
              }
              testId="kpi-adiantamentos"
            />
            <StatCard
              rotulo="Lançamentos do mês"
              valor={`+ ${formatarBRL(paraReais(resumo.adicionaisCentavos)).replace("R$", "").trim()} · − ${formatarBRL(paraReais(resumo.descontosCentavos)).replace("R$", "").trim()}`}
              legenda={`${resumo.nAdicionais} adiciona${resumo.nAdicionais === 1 ? "l" : "is"} · ${resumo.nDescontos} desconto${resumo.nDescontos === 1 ? "" : "s"}`}
              testId="kpi-lancamentos"
            />
          </div>

          {faixaEstado}

          {/* Antes da tabela, sempre: é a pendência que decide se o mês pode ser
              pago. Mês sem quinzena não tem pendência (a faixa acima já diz o
              que fazer) — só visibilidade; a trava do servidor não muda. */}
          <BlocoPendencias
            pendencias={pendencias}
            competencia={competencia}
            quinzenaAberta={pendenciasData?.quinzenaAberta ?? false}
            quinzena={data?.quinzenaDaCompetencia ?? null}
            competenciaTemQuinzena={quinzenas.length > 0}
          />

          {linhas.length === 0 ? (
            <EmptyState titulo="Nenhum mensalista neste mês" descricao="Cadastre alguém como Mensalista em Funcionários, ou navegue para outro mês." />
          ) : (
            <>
              {/* Desktop — tabela */}
              <div className="hidden md:block rounded-lg border bg-card overflow-x-auto">
                <DataTable>
                  <DataTableHeader>
                    <DataTableRow>
                      <DataTableHead className="min-w-[180px]">Funcionário</DataTableHead>
                      <DataTableHead className="text-center">Presença 01–{data?.fim.slice(8, 10)}</DataTableHead>
                      <DataTableHead num>Salário</DataTableHead>
                      <DataTableHead num>Adiantamento</DataTableHead>
                      <DataTableHead num>Adicionais</DataTableHead>
                      <DataTableHead num>Descontos</DataTableHead>
                      <DataTableHead num>Líquido</DataTableHead>
                      {/* Estado e ação grudam à direita — ver COL_ESTADO_FIXA. */}
                      <DataTableHead className={`text-center ${COL_ESTADO_FIXA} ${FIXA_CABECALHO}`}>
                        <span className="relative z-10">Estado</span>
                      </DataTableHead>
                      <DataTableHead className={`${COL_ACAO_FIXA} ${FIXA_CABECALHO}`} />
                    </DataTableRow>
                  </DataTableHeader>
                  <DataTableBody>
                    {linhas.map(l => (
                      <LinhaMensalistaMesDesktop
                        key={l.id}
                        l={l}
                        quinzena={data?.quinzenaDaCompetencia}
                        isAdmin={isAdmin}
                        onAbrir={setAberta}
                        onPago={marcarPago}
                        pagando={atualizar.isPending || pagandoTodos}
                        pendente={idsPendentes.has(l.id)}
                      />
                    ))}
                    <DataTableRow className="font-bold bg-muted/50 border-t-2">
                      <DataTableCell className="text-sm">TOTAL</DataTableCell>
                      <DataTableCell />
                      <DataTableCell className="text-right font-mono text-xs tabular-nums">{formatarBRL(paraReais(totais.salarios))}</DataTableCell>
                      <DataTableCell className="text-right font-mono text-xs tabular-nums text-erro">{totais.adiantamentos > 0 ? `− ${formatarBRL(paraReais(totais.adiantamentos))}` : "—"}</DataTableCell>
                      <DataTableCell className="text-right font-mono text-xs tabular-nums text-sucesso">{totais.adicionais > 0 ? `+ ${formatarBRL(paraReais(totais.adicionais))}` : "—"}</DataTableCell>
                      <DataTableCell className="text-right font-mono text-xs tabular-nums text-erro">{totais.descontos > 0 ? `− ${formatarBRL(paraReais(totais.descontos))}` : "—"}</DataTableCell>
                      <DataTableCell className="text-right font-mono text-sm tabular-nums">{formatarBRL(paraReais(totais.liquido))}</DataTableCell>
                      <DataTableCell className={`${COL_ESTADO_FIXA} ${FIXA_TOTAL}`} />
                      <DataTableCell className={`${COL_ACAO_FIXA} ${FIXA_TOTAL}`} />
                    </DataTableRow>
                  </DataTableBody>
                </DataTable>
                <div className="border-t bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
                  Líquido = salário − adiantamento pago + variável − descontos. Adicionais são pagos na quinzena e aparecem aqui só como informação (teto).
                  Presença e faltas vêm das tarefas do mês civil · nada aqui cria diária.
                </div>
              </div>

              {/* Celular — lista compacta */}
              <div className="md:hidden space-y-2">
                {linhas.map(l => (
                  <LinhaMensalistaMesMobile key={l.id} l={l} quinzena={data?.quinzenaDaCompetencia} onAbrir={setAberta} />
                ))}
                <p className="text-[11px] text-muted-foreground px-1">
                  Líquido = salário − adiantamento pago + variável − descontos. Adicionais saem na quinzena; presença vem das tarefas · nada aqui cria diária.
                </p>
              </div>
            </>
          )}
        </>
      )}

      {/* Celular — barra fixa inferior (só admin, conforme o estado) */}
      {isAdmin && !isLoading && (resumo.estado === "projecao" || resumo.estado === "gerado") && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border px-4 py-3 z-30">
          {resumo.estado === "gerado" ? (
            <button
              type="button"
              onClick={marcarTodos}
              disabled={pagandoTodos || atualizar.isPending}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-3 text-sm font-medium disabled:opacity-50"
            >
              {pagandoTodos && <Loader2 className="h-4 w-4 animate-spin" />}
              Marcar todos como pagos · {formatarBRL(paraReais(resumo.liquidoAPagarCentavos))}
            </button>
          ) : (
            <button
              type="button"
              onClick={onGerar}
              disabled={!podeGerar || gerar.isPending}
              className="w-full flex items-center justify-center gap-2 border border-info/30 text-info rounded-xl py-3 text-sm font-medium disabled:opacity-50"
            >
              <Lock className="h-4 w-4" />
              {podeGerar ? "Gerar fechamento" : quinzenasAbertas.length > 0
                ? `Gerar após fechar a quinzena de ${formatarDataCurta(quinzenasAbertas[quinzenasAbertas.length - 1].dataPagamento ?? quinzenasAbertas[quinzenasAbertas.length - 1].periodoFim)}`
                : "Gerar fechamento"}
            </button>
          )}
        </div>
      )}

      <ExtratoMensalistaSheet
        open={!!linhaAberta}
        onOpenChange={v => { if (!v) setAberta(null); }}
        linha={linhaAberta}
        competencia={competencia}
        inicio={data?.inicio ?? `${competencia}-01`}
        fim={data?.fim ?? `${competencia}-28`}
        dataPagamento={dataPagamento}
        quinzena={data?.quinzenaDaCompetencia}
        isAdmin={isAdmin}
        pendente={linhaAberta ? idsPendentes.has(linhaAberta.id) : false}
      />
    </div>
  );
}
